import React, { useState, useMemo } from "react";
import Downshift from "downshift";
import MenuItem from "@material-ui/core/MenuItem";
import MuiTextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import withStyles from "@material-ui/core/styles/withStyles";
import { FastField, Field } from "formik";
import { getFieldStatus } from "./common";
import { AddPropertyFieldComponent } from "../property/PropertyField";
import { usePromise } from "react-use";
import Grow from "@material-ui/core/Grow";
import { getSuggestions, getSuggestionGeocode } from "../../state/geocode";

function renderInput(inputProps) {
  const { InputProps, classes, ref, ...other } = inputProps;

  return (
    <MuiTextField
      InputProps={{
        inputRef: ref,
        classes: {
          root: classes.inputRoot,
          input: classes.inputInput
        },
        ...InputProps
      }}
      {...other}
    />
  );
}

function renderSuggestion(suggestionProps) {
  const {
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectedItem
  } = suggestionProps;
  const isHighlighted = highlightedIndex === index;
  const isSelected = (selectedItem || "").indexOf(suggestion.description) > -1;

  return (
    <MenuItem
      {...itemProps}
      key={index + "-" + suggestion.description}
      selected={isHighlighted}
      component="div"
      style={{
        fontWeight: isSelected ? 500 : 400
      }}
    >
      {suggestion.description}
    </MenuItem>
  );
}

const AddressDownshift = ({
  fieldVal,
  onChange,
  readOnly,
  classes,
  name,
  label,
  disabled,
  hasError,
  error,
  form,
  field,
  ...props
}) => {
  const mounted = usePromise();

  const [suggestions, setSuggestions] = useState([]);

  const inputOnChange = useMemo(
    () => inputValue => {
      if (!readOnly) {
        form.setFieldValue(name, inputValue);
        form.setFieldTouched(name, true);
      }

      mounted(getSuggestions(inputValue)).then(setSuggestions);
    },
    [
      form,
      name,
      readOnly,
      //form.setFieldValue,
      //form.setFieldTouched,
      mounted,
      //getSuggestions,
      setSuggestions
    ]
  );

  const downshiftOnChange = useMemo(
    () => value => {
      const suggestion = suggestions.find(item => item.description === value);

      mounted(getSuggestionGeocode(suggestion))
        .then(geocode => {
          if (!geocode) return;

          const comps = geocode[0].address_components;
          const x = {};
          comps.forEach(address_component => {
            const types = address_component.types;
            types.forEach(type => {
              switch (type) {
                case "street_number":
                  x[type] = address_component.short_name;
                  break;

                case "route":
                  x[type] = address_component.long_name;
                  break;

                case "locality":
                  x.city = address_component.short_name;
                  break;

                case "administrative_area_level_1":
                  x.state = address_component.short_name;
                  break;

                case "postal_code":
                  x.zip = address_component.short_name;
                  break;

                default:
              }
            });
          });

          x.address1 =
            x.street_number && x.route ? x.street_number + " " + x.route : "";

          onChange && onChange(x);
        })
        .catch(() => {
          onChange && onChange(suggestion);
        });
    },
    [onChange, suggestions, mounted]
  );

  const _inputProps = useMemo(
    () => ({
      onBlur:
        (!readOnly &&
          (() => {
            form.setFieldTouched(name, true);
          })) ||
        undefined
    }),
    [readOnly, form, name]
  );

  return (
    <Downshift
      inputValue={fieldVal}
      selectedItem={fieldVal}
      onChange={downshiftOnChange}
      onInputValueChange={inputOnChange}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        inputValue,
        isOpen,
        selectedItem
      }) => {
        const InputProps = getInputProps(_inputProps);

        return (
          <div className={classes.container}>
            {renderInput({
              classes,
              label,
              InputLabelProps: getLabelProps(),
              InputProps,
              //name // Don't specify name attribute to disable autofill
              error: hasError,
              helperText: hasError && error,
              disabled,
              value: inputValue,
              ...props
            })}

            {isOpen && suggestions && suggestions.length > 0 && (
              <Grow in style={{ transformOrigin: "0 0 0" }}>
                <div {...getMenuProps()}>
                  {isOpen ? (
                    <Paper className={classes.paper} square>
                      {suggestions.map((suggestion, index) =>
                        renderSuggestion({
                          suggestion,
                          index,
                          itemProps: getItemProps({
                            item: suggestion.description
                          }),
                          highlightedIndex,
                          selectedItem
                        })
                      )}
                    </Paper>
                  ) : null}
                </div>
              </Grow>
            )}
          </div>
        );
      }}
    </Downshift>
  );
};

const styles = {
  container: {
    flexGrow: 1,
    position: "relative"
  },
  paper: {
    position: "absolute",
    zIndex: 2,
    left: 0,
    right: 0
  },
  inputRoot: {
    flexWrap: "wrap"
  },
  inputInput: {
    width: "auto",
    flexGrow: 1
  }
};

const AddressField = withStyles(styles, { name: "LcAddressField" })(
  ({
    classes,
    name,
    label: labelProp = name,
    value: valueProp,
    disabled: disabledProp,
    fast = true,
    FieldComponent = fast ? FastField : Field,
    onAddressSelected,
    ...props
  }) => (
    <FieldComponent name={name}>
      {({ field, form }) => {
        const { error, hasError, disabled, label, value } = getFieldStatus(
          form,
          field,
          valueProp,
          labelProp,
          disabledProp
        );

        const fieldVal = value === undefined || value === null ? "" : value;

        const onChange =
          onAddressSelected &&
          (address => onAddressSelected(address, form, field));

        return (
          <AddressDownshift
            fieldVal={fieldVal}
            onChange={onChange}
            classes={classes}
            name={name}
            label={label}
            disabled={disabled}
            form={form}
            field={field}
            error={error}
            hasError={hasError}
            {...props}
          />
        );
      }}
    </FieldComponent>
  )
);

AddPropertyFieldComponent("address", AddressField);

export default AddressField;
